import React from "react";
import Select from "react-select";
import { DelDatePicker } from "../common/DelDatePicker";
import { LIST_OF_STATE_INITIALS } from "../../constants/wasteDisposalConstants";

import styles from "./wasteDisposalComponents.module.css";

/**
 * Helper to get field error for waste disposal fields
 * @param {Object} autosaveErrors - The autosaveErrors state from callbacks
 * @param {string} patchType - The patch type (waste_disposal, waste_disposal_container_info, etc.)
 * @param {string} fieldName - The field name (e.g., "client_name")
 * @returns {string|null} Error message or null
 */
const getWdFieldError = (autosaveErrors, patchType, fieldName) => {
  const key = `${patchType}.${fieldName}`;
  return autosaveErrors?.fieldErrors?.[key] || null;
};

/**
 * Helper to get row error for waste disposal row-based sections
 * @param {Object} autosaveErrors - The autosaveErrors state from callbacks
 * @param {string} rowId - UUID of the row
 * @returns {Object|null} Error object mapping field names to error messages, or null
 */
const getWdRowError = (autosaveErrors, rowId) => {
  if (!autosaveErrors?.rowErrors) return null;
  return autosaveErrors.rowErrors[rowId] || null;
};

// State Select Component
export const StateSelect = ({ callbacks }) => {
  // Setting the state select options
  const stateSelectOptions = LIST_OF_STATE_INITIALS.map((state) => ({
    value: state,
    label: state,
  }));

  // Handling the selection of a state - emit immediately on change
  const handleStateSelect = (e) => {
    callbacks.basicInfoActions.updateWdState(
      callbacks.wdData,
      e?.value || "",
      callbacks.emitPatch
    );
  };

  return (
    <Select
      className={styles.stateSelect}
      value={{
        value: callbacks.wdData.basicInfo.state || "",
        label: callbacks.wdData.basicInfo.state || "",
      }}
      options={stateSelectOptions}
      onChange={handleStateSelect}
      placeholder="Select a state"
      isSearchable={true}
      isClearable={true}
      isDisabled={callbacks.wdMode === "view"}
      menuPlacement="auto"
      menuPosition="fixed"
      menuPortalTarget={document.body}
    />
  );
};

// Waste Disposal Basic Info Tab
export const WasteDisposalBasicInfoTab = ({ callbacks }) => {
  // Get autosave errors for this section
  const autosaveErrors = callbacks.autosaveErrors || {};

  // Helper to get error for a specific field
  const getError = (fieldName) => getWdFieldError(autosaveErrors, "waste_disposal", fieldName);

  // Handling updating client names - local state only
  const handleUpdateClientName = (e) => {
    callbacks.basicInfoActions.updateWdClientName(
      callbacks.wdData,
      e.target.value,
      null
    );
  };
  // Emit on blur
  const handleBlurClientName = (e) => {
    callbacks.basicInfoActions.updateWdClientName(
      callbacks.wdData,
      e.target.value,
      callbacks.emitPatch
    );
  };

  // Handling updating generator names - local state only
  const handleUpdateGeneratorName = (e) => {
    callbacks.basicInfoActions.updateWdGeneratorName(
      callbacks.wdData,
      e.target.value,
      null
    );
  };
  // Emit on blur
  const handleBlurGeneratorName = (e) => {
    callbacks.basicInfoActions.updateWdGeneratorName(
      callbacks.wdData,
      e.target.value,
      callbacks.emitPatch
    );
  };

  // Handling updating disposal dates - emit immediately on change
  const handleUpdateDisposalDate = (date) => {
    callbacks.basicInfoActions.updateWdDisposalDate(
      callbacks.wdData,
      date,
      callbacks.emitPatch
    );
  };

  // Handling updating contact - local state only
  const handleUpdateContact = (e) => {
    callbacks.basicInfoActions.updateWdContact(
      callbacks.wdData,
      e.target.value,
      null
    );
  };
  // Emit on blur
  const handleBlurContact = (e) => {
    callbacks.basicInfoActions.updateWdContact(
      callbacks.wdData,
      e.target.value,
      callbacks.emitPatch
    );
  };

  // Handling the phone number update - local state only
  const handleUpdatePhoneNumber = (e) => {
    callbacks.basicInfoActions.updateWdPhoneNumber(
      callbacks.wdData,
      e.target.value,
      null
    );
  };
  // Emit on blur
  const handleBlurPhoneNumber = (e) => {
    callbacks.basicInfoActions.updateWdPhoneNumber(
      callbacks.wdData,
      e.target.value,
      callbacks.emitPatch
    );
  };

  // Handling the fax number update - local state only
  const handleUpdateFaxNumber = (e) => {
    callbacks.basicInfoActions.updateWdFaxNumber(
      callbacks.wdData,
      e.target.value,
      null
    );
  };
  // Emit on blur
  const handleBlurFaxNumber = (e) => {
    callbacks.basicInfoActions.updateWdFaxNumber(
      callbacks.wdData,
      e.target.value,
      callbacks.emitPatch
    );
  };

  // Handling the street address update - local state only
  const handleUpdateStreetAddress = (e) => {
    callbacks.basicInfoActions.updateWdStreetAddress(
      callbacks.wdData,
      e.target.value,
      null
    );
  };
  // Emit on blur
  const handleBlurStreetAddress = (e) => {
    callbacks.basicInfoActions.updateWdStreetAddress(
      callbacks.wdData,
      e.target.value,
      callbacks.emitPatch
    );
  };

  // Handling the city update - local state only
  const handleUpdateCity = (e) => {
    callbacks.basicInfoActions.updateWdCity(
      callbacks.wdData,
      e.target.value,
      null
    );
  };
  // Emit on blur
  const handleBlurCity = (e) => {
    callbacks.basicInfoActions.updateWdCity(
      callbacks.wdData,
      e.target.value,
      callbacks.emitPatch
    );
  };

  // Handling the zipcode update - local state only
  const handleUpdateZipcode = (e) => {
    callbacks.basicInfoActions.updateWdZipcode(
      callbacks.wdData,
      e.target.value,
      null
    );
  };
  // Emit on blur
  const handleBlurZipcode = (e) => {
    callbacks.basicInfoActions.updateWdZipcode(
      callbacks.wdData,
      e.target.value,
      callbacks.emitPatch
    );
  };

  return (
    <div className={styles.wasteDisposalBasicInfoTabContainer}>
      <div className={styles.wasteDisposalBasicInfoRow}>
        <div>
          <label className={styles.inputLabel}>Client Name</label>
          <input
            type="text"
            placeholder="Client Name"
            value={callbacks.wdData.basicInfo.clientName}
            onChange={handleUpdateClientName}
            onBlur={handleBlurClientName}
            disabled={callbacks.wdMode === "view"}
            className={`${styles.wasteDisposalBasicInfoInput} ${getError("client_name") ? styles.inputError : ""}`}
          />
          {getError("client_name") && <span className={styles.errorText}>{getError("client_name")}</span>}
        </div>
        <div>
          <label className={styles.inputLabel}>Generator Name</label>
          <input
            type="text"
            placeholder="Generator Name"
            value={callbacks.wdData.basicInfo.generatorName}
            onChange={handleUpdateGeneratorName}
            onBlur={handleBlurGeneratorName}
            disabled={callbacks.wdMode === "view"}
            className={`${styles.wasteDisposalBasicInfoInput} ${getError("generator_name") ? styles.inputError : ""}`}
          />
          {getError("generator_name") && <span className={styles.errorText}>{getError("generator_name")}</span>}
        </div>
        <div>
          <label className={styles.inputLabel}>Date</label>
          <DelDatePicker
            selected={callbacks.wdData.basicInfo.disposalDate}
            onChange={handleUpdateDisposalDate}
            className={`${styles.dateInput} ${getError("disposal_date") ? styles.inputError : ""}`}
            dateFormat="MM/dd/yyyy"
            placeholderText="Select a date"
            isClearable={true}
            isDisabled={callbacks.wdMode === "view"}
            showPopperArrow={false}
            withPortal
            portalId="date-picker-portal"
            calendarClassName={styles.calendar}
            wrapperClassName={styles.datePickerWrapper}
          />
          {getError("disposal_date") && <span className={styles.errorText}>{getError("disposal_date")}</span>}
        </div>
      </div>
      <div className={styles.wasteDisposalBasicInfoRow}>
        <div>
          <label className={styles.inputLabel}>Contact</label>
          <input
            type="text"
            placeholder="Name of Contact"
            value={callbacks.wdData.basicInfo.contact}
            onChange={handleUpdateContact}
            onBlur={handleBlurContact}
            disabled={callbacks.wdMode === "view"}
            className={`${styles.wasteDisposalBasicInfoInput} ${getError("contact") ? styles.inputError : ""}`}
          />
          {getError("contact") && <span className={styles.errorText}>{getError("contact")}</span>}
        </div>
        <div>
          <label className={styles.inputLabel}>Phone Number</label>
          <input
            type="tel"
            placeholder="Phone Number"
            value={callbacks.wdData.basicInfo.phoneNumber}
            onChange={handleUpdatePhoneNumber}
            onBlur={handleBlurPhoneNumber}
            disabled={callbacks.wdMode === "view"}
            className={`${styles.wasteDisposalBasicInfoInput} ${getError("phone_number") ? styles.inputError : ""}`}
          />
          {getError("phone_number") && <span className={styles.errorText}>{getError("phone_number")}</span>}
        </div>
        <div>
          <label className={styles.inputLabel}>Fax Number</label>
          <input
            type="tel"
            placeholder="Fax Number"
            value={callbacks.wdData.basicInfo.faxNumber}
            onChange={handleUpdateFaxNumber}
            onBlur={handleBlurFaxNumber}
            disabled={callbacks.wdMode === "view"}
            className={`${styles.wasteDisposalBasicInfoInput} ${getError("fax_number") ? styles.inputError : ""}`}
          />
          {getError("fax_number") && <span className={styles.errorText}>{getError("fax_number")}</span>}
        </div>
      </div>
      <div className={styles.wasteDisposalBasicInfoRow}>
        <div>
          <label className={styles.inputLabel}>Street Address</label>
          <input
            type="text"
            placeholder="Street Address"
            value={callbacks.wdData.basicInfo.streetAddress}
            onChange={handleUpdateStreetAddress}
            onBlur={handleBlurStreetAddress}
            disabled={callbacks.wdMode === "view"}
            className={`${styles.wasteDisposalBasicInfoInput} ${getError("street_address") ? styles.inputError : ""}`}
          />
          {getError("street_address") && <span className={styles.errorText}>{getError("street_address")}</span>}
        </div>
        <div>
          <label className={styles.inputLabel}>City</label>
          <input
            type="text"
            placeholder="City"
            value={callbacks.wdData.basicInfo.city}
            onChange={handleUpdateCity}
            onBlur={handleBlurCity}
            disabled={callbacks.wdMode === "view"}
            className={`${styles.wasteDisposalBasicInfoInput} ${getError("city") ? styles.inputError : ""}`}
          />
          {getError("city") && <span className={styles.errorText}>{getError("city")}</span>}
        </div>
        <div>
          <label className={styles.inputLabel}>State</label>
          <StateSelect callbacks={callbacks} />
          {getError("state") && <span className={styles.errorText}>{getError("state")}</span>}
        </div>
        <div>
          <label className={styles.inputLabel}>Zip Code</label>
          <input
            type="text"
            placeholder="Zip Code"
            value={callbacks.wdData.basicInfo.zipcode}
            onChange={handleUpdateZipcode}
            onBlur={handleBlurZipcode}
            disabled={callbacks.wdMode === "view"}
            className={`${styles.wasteDisposalBasicInfoInput} ${getError("zipcode") ? styles.inputError : ""}`}
          />
          {getError("zipcode") && <span className={styles.errorText}>{getError("zipcode")}</span>}
        </div>
      </div>
    </div>
  );
};

// Waste Disposal Container Table Row
export const WasteDisposalContainerTableRow = ({
  callbacks,
  containerInfo,
}) => {
  // Get row errors for this container
  const rowError = getWdRowError(callbacks.autosaveErrors, containerInfo.rowId);
  const hasRowError = rowError && Object.keys(rowError).length > 0;

  // Handling the removal of a container - emit immediately
  const handleRemoveContainer = () => {
    callbacks.wasteInfoActions.removeWasteDisposalContainerTableRow(
      callbacks.wdData,
      containerInfo.rowId,
      callbacks.emitPatch
    );
  };

  // Handling the update of a container info - local state only
  const handleUpdateContainer = (e) => {
    callbacks.wasteInfoActions.updateWdContainer(
      callbacks.wdData,
      e.target.value,
      containerInfo.rowId,
      null
    );
  };
  // Emit on blur
  const handleBlurContainer = (e) => {
    callbacks.wasteInfoActions.updateWdContainer(
      callbacks.wdData,
      e.target.value,
      containerInfo.rowId,
      callbacks.emitPatch
    );
  };

  // Handling the update of the amount of a container - local state only
  const handleUpdateContainerAmount = (e) => {
    callbacks.wasteInfoActions.updateWdContainerAmount(
      callbacks.wdData,
      e.target.value,
      containerInfo.rowId,
      null
    );
  };
  // Emit on blur
  const handleBlurContainerAmount = (e) => {
    callbacks.wasteInfoActions.updateWdContainerAmount(
      callbacks.wdData,
      e.target.value,
      containerInfo.rowId,
      callbacks.emitPatch
    );
  };

  // Handling the update of the waste type of a container - local state only
  const handleUpdateContainerWasteType = (e) => {
    callbacks.wasteInfoActions.updateWdContainerWasteType(
      callbacks.wdData,
      e.target.value,
      containerInfo.rowId,
      null
    );
  };
  // Emit on blur
  const handleBlurContainerWasteType = (e) => {
    callbacks.wasteInfoActions.updateWdContainerWasteType(
      callbacks.wdData,
      e.target.value,
      containerInfo.rowId,
      callbacks.emitPatch
    );
  };

  return (
    <>
      <tr className={`${styles.wasteDisposalContainerTableRow} ${hasRowError ? styles.rowError : ""}`}>
        <td data-label="Container">
          <input
            type="text"
            placeholder="Container"
            value={containerInfo.container}
            onChange={handleUpdateContainer}
            onBlur={handleBlurContainer}
            disabled={callbacks.wdMode === "view"}
            className={rowError?.container ? styles.inputError : ""}
          />
          {rowError?.container && <span className={styles.errorText}>{rowError.container}</span>}
        </td>
        <td data-label="Amount">
          <input
            type="text"
            placeholder="Amount"
            value={containerInfo.amount}
            onChange={handleUpdateContainerAmount}
            onBlur={handleBlurContainerAmount}
            disabled={callbacks.wdMode === "view"}
            className={rowError?.amount ? styles.inputError : ""}
          />
          {rowError?.amount && <span className={styles.errorText}>{rowError.amount}</span>}
        </td>
        <td data-label="Type of Waste">
          <input
            type="text"
            placeholder="Type of Waste"
            value={containerInfo.wasteType}
            onChange={handleUpdateContainerWasteType}
            onBlur={handleBlurContainerWasteType}
            disabled={callbacks.wdMode === "view"}
            className={rowError?.waste_type ? styles.inputError : ""}
          />
          {rowError?.waste_type && <span className={styles.errorText}>{rowError.waste_type}</span>}
        </td>
        <td data-label="Actions">
          <button
            onClick={handleRemoveContainer}
            className={styles.wasteDisposalContainerTableRowButton}
            disabled={callbacks.wdMode === "view"}
          >
            Remove
          </button>
        </td>
      </tr>
    </>
  );
};

// Waste Disposal Container Table
export const WasteDisposalContainerTable = ({ callbacks }) => {
  // Handling the addition of a container - emit immediately
  const handleAddContainer = () => {
    callbacks.wasteInfoActions.addWasteDisposalContainerTableRow(
      callbacks.wdData,
      callbacks.emitPatch
    );
  };

  return (
    <div className={styles.wasteDisposalContainerTableWrapper}>
      <div className={styles.wasteDisposalWasteContainerTableTitle}>
        <h3>Containers</h3>
      </div>
      <table className={styles.wasteDisposalContainerTable}>
        <thead>
          <tr>
            <th>Container</th>
            <th>Amount</th>
            <th>Type of Waste</th>
          </tr>
        </thead>
        <tbody>
          {callbacks.wdData.containerInfo.map((containerInfo) => (
            <WasteDisposalContainerTableRow
              key={containerInfo.rowId}
              callbacks={callbacks}
              containerInfo={containerInfo}
            />
          ))}
        </tbody>
      </table>
      <div className={styles.wasteDisposalContainerTableButtons}>
        <button
          onClick={handleAddContainer}
          disabled={callbacks.wdMode === "view"}
        >
          Add Row
        </button>
      </div>
    </div>
  );
};

// Waste Disposal Constituent Table Row
export const WasteDisposalConstituentTableRow = ({
  callbacks,
  constituent,
}) => {
  // Get row errors for this constituent
  const rowError = getWdRowError(callbacks.autosaveErrors, constituent.rowId);
  const hasRowError = rowError && Object.keys(rowError).length > 0;

  // Handling the update of a constituent - local state only
  const handleUpdateConstituent = (e) => {
    callbacks.wasteInfoActions.updateWdConstituent(
      callbacks.wdData,
      e.target.value,
      constituent.rowId,
      null
    );
  };
  // Emit on blur
  const handleBlurConstituent = (e) => {
    callbacks.wasteInfoActions.updateWdConstituent(
      callbacks.wdData,
      e.target.value,
      constituent.rowId,
      callbacks.emitPatch
    );
  };

  // Handling the update of a constituent's concentration range - local state only
  const handleUpdateConstituentConcentrationRange = (e) => {
    callbacks.wasteInfoActions.updateWdConstituentConcentrationRange(
      callbacks.wdData,
      e.target.value,
      constituent.rowId,
      null
    );
  };
  // Emit on blur
  const handleBlurConstituentConcentrationRange = (e) => {
    callbacks.wasteInfoActions.updateWdConstituentConcentrationRange(
      callbacks.wdData,
      e.target.value,
      constituent.rowId,
      callbacks.emitPatch
    );
  };

  // Handling remove table row - emit immediately
  const handleRemoveTableRow = () => {
    callbacks.wasteInfoActions.removeWasteDisposalConstituentsTableRow(
      callbacks.wdData,
      constituent.rowId,
      callbacks.emitPatch
    );
  };

  return (
    <>
      <tr className={`${styles.wasteDisposalConstituentTableRow} ${hasRowError ? styles.rowError : ""}`}>
        <td data-label="Constituent">
          <input
            type="text"
            placeholder="Constituent"
            value={constituent.constituent}
            onChange={handleUpdateConstituent}
            onBlur={handleBlurConstituent}
            disabled={callbacks.wdMode === "view"}
            className={rowError?.constituent ? styles.inputError : ""}
          />
          {rowError?.constituent && <span className={styles.errorText}>{rowError.constituent}</span>}
        </td>
        <td data-label="Concentration Range">
          <input
            type="text"
            placeholder="Concentration Range"
            value={constituent.concentrationRange}
            onChange={handleUpdateConstituentConcentrationRange}
            onBlur={handleBlurConstituentConcentrationRange}
            disabled={callbacks.wdMode === "view"}
            className={rowError?.concentration_range ? styles.inputError : ""}
          />
          {rowError?.concentration_range && <span className={styles.errorText}>{rowError.concentration_range}</span>}
        </td>
        <td data-label="Actions">
          <button
            onClick={handleRemoveTableRow}
            className={styles.wasteDisposalConstituentTableRowButton}
            disabled={callbacks.wdMode === "view"}
          >
            Remove
          </button>
        </td>
      </tr>
    </>
  );
};

// Waste Disposal Constituent Table
export const WasteDisposalConstituentTable = ({ callbacks }) => {
  // Handling the addition of a constituent - emit immediately
  const handleAddConstituent = () => {
    callbacks.wasteInfoActions.addWasteDisposalConstituentsTableRow(
      callbacks.wdData,
      callbacks.emitPatch
    );
  };

  return (
    <div className={styles.wasteDisposalConstituentTableWrapper}>
      <div className={styles.wasteDisposalWasteContainerTableTitle}>
        <h3>Constituents</h3>
      </div>
      <table className={styles.wasteDisposalConstituentTable}>
        <thead>
          <tr>
            <th>Constituent</th>
            <th>Concentration Range</th>
          </tr>
        </thead>
        <tbody>
          {callbacks.wdData.constituents.map((constituent) => (
            <WasteDisposalConstituentTableRow
              key={constituent.rowId}
              callbacks={callbacks}
              constituent={constituent}
            />
          ))}
        </tbody>
      </table>
      <div className={styles.wasteDisposalConstituentTableButtons}>
        <button
          onClick={handleAddConstituent}
          disabled={callbacks.wdMode === "view"}
        >
          Add Row
        </button>
      </div>
    </div>
  );
};

// Waste Disposal Waste Info Tab
export const WasteDisposalWasteInfoTab = ({ callbacks }) => {
  // Get autosave errors for this section
  const autosaveErrors = callbacks.autosaveErrors || {};

  // Helper to get error for a specific field
  const getError = (fieldName) => getWdFieldError(autosaveErrors, "waste_disposal", fieldName);

  // Handling name of waste update - local state only
  const handleUpdateNameOfWaste = (e) => {
    callbacks.wasteInfoActions.updateWdNameOfWaste(
      callbacks.wdData,
      e.target.value,
      null
    );
  };
  // Emit on blur
  const handleBlurNameOfWaste = (e) => {
    callbacks.wasteInfoActions.updateWdNameOfWaste(
      callbacks.wdData,
      e.target.value,
      callbacks.emitPatch
    );
  };

  // Handling process generating update - local state only
  const handleUpdateProcessGenerating = (e) => {
    callbacks.wasteInfoActions.updateWdProcessGenerating(
      callbacks.wdData,
      e.target.value,
      null
    );
  };
  // Emit on blur
  const handleBlurProcessGenerating = (e) => {
    callbacks.wasteInfoActions.updateWdProcessGenerating(
      callbacks.wdData,
      e.target.value,
      callbacks.emitPatch
    );
  };

  return (
    <div className={styles.wasteDisposalWasteInfoTabContainer}>
      <div className={styles.wasteDisposalWasteInfoRow}>
        <label className={styles.inputLabel}>Name of Waste</label>
        <input
          type="text"
          placeholder="Name of Waste"
          value={callbacks.wdData.basicInfo.nameOfWaste}
          onChange={handleUpdateNameOfWaste}
          onBlur={handleBlurNameOfWaste}
          disabled={callbacks.wdMode === "view"}
          className={`${styles.wasteDisposalWasteInfoInput} ${getError("name_of_waste") ? styles.inputError : ""}`}
        />
        {getError("name_of_waste") && <span className={styles.errorText}>{getError("name_of_waste")}</span>}
      </div>
      <div className={styles.wasteDisposalWasteInfoRow}>
        <label className={styles.inputLabel}>Process Generating</label>
        <textarea
          placeholder="Process Generating"
          value={callbacks.wdData.basicInfo.processGenerating}
          onChange={handleUpdateProcessGenerating}
          onBlur={handleBlurProcessGenerating}
          disabled={callbacks.wdMode === "view"}
          className={`${styles.wasteDisposalWasteInfoInput} ${getError("process_generating") ? styles.inputError : ""}`}
        />
        {getError("process_generating") && <span className={styles.errorText}>{getError("process_generating")}</span>}
      </div>
      <WasteDisposalContainerTable callbacks={callbacks} />
      <WasteDisposalConstituentTable callbacks={callbacks} />
    </div>
  );
};

// Waste Disposal Paper Work Tab
export const WasteDisposalPaperWorkTab = ({ callbacks }) => {
  // Get autosave errors for this section
  const autosaveErrors = callbacks.autosaveErrors || {};

  // Helper to get error for a specific field
  const getError = (fieldName) => getWdFieldError(autosaveErrors, "waste_disposal", fieldName);

  // Handling samples taken yes update - emit immediately on change
  const handleUpdateSamplesTakenYes = (e) => {
    callbacks.paperWorkActions.updateWdSamplesTakenYes(
      callbacks.wdData,
      e.target.checked,
      callbacks.emitPatch
    );
  };

  // Handling samples taken no update - emit immediately on change
  const handleUpdateSamplesTakenNo = (e) => {
    callbacks.paperWorkActions.updateWdSamplesTakenNo(
      callbacks.wdData,
      e.target.checked,
      callbacks.emitPatch
    );
  };

  // Handling samples sent to update - local state only
  const handleUpdateSamplesSentTo = (e) => {
    callbacks.paperWorkActions.updateWdSamplesSentTo(
      callbacks.wdData,
      e.target.value,
      null
    );
  };
  // Emit on blur
  const handleBlurSamplesSentTo = (e) => {
    callbacks.paperWorkActions.updateWdSamplesSentTo(
      callbacks.wdData,
      e.target.value,
      callbacks.emitPatch
    );
  };

  // Handling chain of custody yes update - emit immediately on change
  const handleUpdateChainOfCustodyYes = (e) => {
    callbacks.paperWorkActions.updateWdChainOfCustodyYes(
      callbacks.wdData,
      e.target.checked,
      callbacks.emitPatch
    );
  };

  // Handling chain of custody no update - emit immediately on change
  const handleUpdateChainOfCustodyNo = (e) => {
    callbacks.paperWorkActions.updateWdChainOfCustodyNo(
      callbacks.wdData,
      e.target.checked,
      callbacks.emitPatch
    );
  };

  // Handling msds yes update - emit immediately on change
  const handleUpdateMsdsYes = (e) => {
    callbacks.paperWorkActions.updateWdMsdsYes(
      callbacks.wdData,
      e.target.checked,
      callbacks.emitPatch
    );
  };

  // Handling msds no update - emit immediately on change
  const handleUpdateMsdsNo = (e) => {
    callbacks.paperWorkActions.updateWdMsdsNo(
      callbacks.wdData,
      e.target.checked,
      callbacks.emitPatch
    );
  };

  // Handling letter of agency Yes - emit immediately on change
  const handleUpdateLetterOfAgencyYes = (e) => {
    callbacks.paperWorkActions.updateWdLetterOfAgencyYes(
      callbacks.wdData,
      e.target.checked,
      callbacks.emitPatch
    );
  };

  // Handling letter of agency No - emit immediately on change
  const handleUpdateLetterOfAgencyNo = (e) => {
    callbacks.paperWorkActions.updateWdLetterOfAgencyNo(
      callbacks.wdData,
      e.target.checked,
      callbacks.emitPatch
    );
  };

  // Handling letter of agency Na - emit immediately on change
  const handleUpdateLetterOfAgencyNa = (e) => {
    callbacks.paperWorkActions.updateWdLetterOfAgencyNa(
      callbacks.wdData,
      e.target.checked,
      callbacks.emitPatch
    );
  };

  // Handling location of containers - local state only
  const handleUpdateLocationOfContainers = (e) => {
    callbacks.paperWorkActions.updateWdLocationOfContainers(
      callbacks.wdData,
      e.target.value,
      null
    );
  };
  // Emit on blur
  const handleBlurLocationOfContainers = (e) => {
    callbacks.paperWorkActions.updateWdLocationOfContainers(
      callbacks.wdData,
      e.target.value,
      callbacks.emitPatch
    );
  };

  // Handling comments - local state only
  const handleUpdateComments = (e) => {
    callbacks.paperWorkActions.updateWdComments(
      callbacks.wdData,
      e.target.value,
      null
    );
  };
  // Emit on blur
  const handleBlurComments = (e) => {
    callbacks.paperWorkActions.updateWdComments(
      callbacks.wdData,
      e.target.value,
      callbacks.emitPatch
    );
  };

  return (
    <div className={styles.wasteDisposalPaperWorkTabContainer}>
      <div className={styles.wasteDisposalPaperWorkRow}>
        <div className={styles.wasteDisposalCheckboxSection}>
          <div className={styles.wasteDisposalHeader}>
            <label className={styles.sectionLabel}>Samples Taken</label>
          </div>
          <div className={styles.wasteDisposalPaperWorkCheckboxRow}>
            <div className={styles.wasteDisposalPaperWorkCheckboxInput}>
              <div className={styles.wasteDisposalPaperWorkCheckboxRowLabel}>
                <label>Yes</label>
              </div>
              <input
                type="checkbox"
                checked={callbacks.wdData.basicInfo.samplesTakenYes}
                onChange={handleUpdateSamplesTakenYes}
                className={styles.checkboxInput}
                disabled={callbacks.wdMode === "view"}
              />
            </div>
            <div className={styles.wasteDisposalPaperWorkCheckboxInput}>
              <div className={styles.wasteDisposalPaperWorkCheckboxRowLabel}>
                <label>No</label>
              </div>
              <input
                type="checkbox"
                checked={callbacks.wdData.basicInfo.samplesTakenNo}
                onChange={handleUpdateSamplesTakenNo}
                className={styles.checkboxInput}
                disabled={callbacks.wdMode === "view"}
              />
            </div>
          </div>
        </div>
        <div className={styles.wasteDisposalBasicInfoRow}>
          <label className={styles.sectionLabel}>Samples Sent To</label>
          <input
            type="text"
            placeholder="Samples Sent To"
            value={callbacks.wdData.basicInfo.samplesSentTo}
            onChange={handleUpdateSamplesSentTo}
            onBlur={handleBlurSamplesSentTo}
            disabled={callbacks.wdMode === "view"}
            className={`${styles.wasteDisposalPaperWorkInput} ${getError("samples_sent_to") ? styles.inputError : ""}`}
          />
          {getError("samples_sent_to") && <span className={styles.errorText}>{getError("samples_sent_to")}</span>}
        </div>
      </div>

      <div className={styles.wasteDisposalPaperWorkCheckboxRow}>
        <div className={styles.wasteDisposalCheckboxSection}>
          <div className={styles.wasteDisposalHeader}>
            <label className={styles.sectionLabel}>Chain of Custody</label>
          </div>
          <div className={styles.wasteDisposalPaperWorkCheckboxRow}>
            <div className={styles.wasteDisposalPaperWorkCheckboxInput}>
              <div className={styles.wasteDisposalPaperWorkCheckboxRowLabel}>
                <label>Yes</label>
              </div>
              <input
                type="checkbox"
                checked={callbacks.wdData.basicInfo.chainOfCustodyYes}
                onChange={handleUpdateChainOfCustodyYes}
                className={styles.checkboxInput}
                disabled={callbacks.wdMode === "view"}
              />
            </div>
            <div className={styles.wasteDisposalPaperWorkCheckboxInput}>
              <div className={styles.wasteDisposalPaperWorkCheckboxRowLabel}>
                <label>No</label>
              </div>
              <input
                type="checkbox"
                checked={callbacks.wdData.basicInfo.chainOfCustodyNo}
                onChange={handleUpdateChainOfCustodyNo}
                className={styles.checkboxInput}
                disabled={callbacks.wdMode === "view"}
              />
            </div>
          </div>
        </div>

        <div className={styles.wasteDisposalCheckboxSection}>
          <div className={styles.wasteDisposalHeader}>
            <label className={styles.sectionLabel}>MSDS</label>
          </div>
          <div className={styles.wasteDisposalPaperWorkCheckboxRow}>
            <div className={styles.wasteDisposalPaperWorkCheckboxInput}>
              <div className={styles.wasteDisposalPaperWorkCheckboxRowLabel}>
                <label>Yes</label>
              </div>
              <input
                type="checkbox"
                checked={callbacks.wdData.basicInfo.msdsYes}
                onChange={handleUpdateMsdsYes}
                className={styles.checkboxInput}
                disabled={callbacks.wdMode === "view"}
              />
            </div>
            <div className={styles.wasteDisposalPaperWorkCheckboxInput}>
              <div className={styles.wasteDisposalPaperWorkCheckboxRowLabel}>
                <label>No</label>
              </div>
              <input
                type="checkbox"
                checked={callbacks.wdData.basicInfo.msdsNo}
                onChange={handleUpdateMsdsNo}
                className={styles.checkboxInput}
                disabled={callbacks.wdMode === "view"}
              />
            </div>
          </div>
        </div>

        <div className={styles.wasteDisposalCheckboxSection}>
          <div className={styles.wasteDisposalHeader}>
            <label className={styles.sectionLabel}>Letter of Agency</label>
          </div>
          <div className={styles.wasteDisposalPaperWorkCheckboxRow}>
            <div className={styles.wasteDisposalPaperWorkCheckboxInput}>
              <div className={styles.wasteDisposalPaperWorkCheckboxRowLabel}>
                <label>Yes</label>
              </div>
              <input
                type="checkbox"
                checked={callbacks.wdData.basicInfo.letterOfAgencyYes}
                onChange={handleUpdateLetterOfAgencyYes}
                className={styles.checkboxInput}
                disabled={callbacks.wdMode === "view"}
              />
            </div>
            <div className={styles.wasteDisposalPaperWorkCheckboxInput}>
              <div className={styles.wasteDisposalPaperWorkCheckboxRowLabel}>
                <label>No</label>
              </div>
              <input
                type="checkbox"
                checked={callbacks.wdData.basicInfo.letterOfAgencyNo}
                onChange={handleUpdateLetterOfAgencyNo}
                className={styles.checkboxInput}
                disabled={callbacks.wdMode === "view"}
              />
            </div>
            <div className={styles.wasteDisposalPaperWorkCheckboxInput}>
              <div className={styles.wasteDisposalPaperWorkCheckboxRowLabel}>
                <label>N/A</label>
              </div>
              <input
                type="checkbox"
                checked={callbacks.wdData.basicInfo.letterOfAgencyNa}
                onChange={handleUpdateLetterOfAgencyNa}
                className={styles.checkboxInput}
                disabled={callbacks.wdMode === "view"}
              />
            </div>
          </div>
        </div>
      </div>

      <div className={styles.wasteDisposalBasicInfoRow}>
        <label className={styles.sectionLabel}>Location of Containers</label>
        <input
          type="text"
          placeholder="Location of Containers"
          value={callbacks.wdData.basicInfo.locationOfContainers}
          onChange={handleUpdateLocationOfContainers}
          onBlur={handleBlurLocationOfContainers}
          disabled={callbacks.wdMode === "view"}
          className={`${styles.wasteDisposalPaperWorkInput} ${getError("location_of_containers") ? styles.inputError : ""}`}
        />
        {getError("location_of_containers") && <span className={styles.errorText}>{getError("location_of_containers")}</span>}
      </div>

      <div className={styles.wasteDisposalBasicInfoRow}>
        <label className={styles.sectionLabel}>Comments</label>
        <textarea
          placeholder="Comments"
          value={callbacks.wdData.basicInfo.comments}
          onChange={handleUpdateComments}
          onBlur={handleBlurComments}
          disabled={callbacks.wdMode === "view"}
          className={`${styles.wasteDisposalPaperWorkInput} ${getError("comments") ? styles.inputError : ""}`}
        />
        {getError("comments") && <span className={styles.errorText}>{getError("comments")}</span>}
      </div>
    </div>
  );
};

// All Waste Disposal Components
export const AllWasteDisposalComponents = ({ callbacks }) => {
  return (
    <div className={styles.allWasteDisposalContent}>
      <div className={styles.wasteDisposalComponentSection}>
        <h2 className={styles.sectionTitle}>Basic Information</h2>
        <WasteDisposalBasicInfoTab callbacks={callbacks} />
      </div>

      <div className={styles.wasteDisposalComponentSection}>
        <h2 className={styles.sectionTitle}>Waste Information</h2>
        <WasteDisposalWasteInfoTab callbacks={callbacks} />
      </div>

      <div className={styles.wasteDisposalComponentSection}>
        <h2 className={styles.sectionTitle}>Paper Work</h2>
        <WasteDisposalPaperWorkTab callbacks={callbacks} />
      </div>
    </div>
  );
};

// Waste Disposal Tab Buttons
export const WasteDisposalTabButtons = ({ tabName, callbacks }) => {
  // Handling the change of tabs
  const handleTabChange = () => {
    callbacks.baseActions.setActiveWasteDisposalTab(tabName);
  };

  return (
    <button
      className={`${styles.tabButton} ${
        callbacks.activeWasteDisposalTab === tabName ? styles.active : ""
      }`}
      onClick={handleTabChange}
    >
      {tabName}
    </button>
  );
};

// Waste Disposal Tab
export const WasteDisposalTab = ({ callbacks, actions }) => {
  const wdState = {
    // Data
    wdData: callbacks.wdData,
    wdMode: callbacks.wdMode,
    activeWasteDisposalTab: callbacks.wdBase.activeWasteDisposalTab,
    emitPatch: callbacks.emitPatch,
    autosaveErrors: callbacks.autosaveErrors,
    // Actions
    baseActions: actions.base,
    basicInfoActions: actions.basicInfo,
    wasteInfoActions: actions.wasteInfo,
    paperWorkActions: actions.paperWork,
  };
  return (
    <div className={styles.wasteDisposalTabContainer}>
      <div className={styles.wasteDisposalTabMenuBar}>
        <WasteDisposalTabButtons tabName="All" callbacks={wdState} />
        <WasteDisposalTabButtons tabName="Basic Info" callbacks={wdState} />
        <WasteDisposalTabButtons tabName="Waste Info" callbacks={wdState} />
        <WasteDisposalTabButtons tabName="Paper Work" callbacks={wdState} />
      </div>
      <div className={styles.wasteDisposalTabContent}>
        {(() => {
          switch (wdState.activeWasteDisposalTab) {
            case "All":
              return <AllWasteDisposalComponents callbacks={wdState} />;
            case "Basic Info":
              return <WasteDisposalBasicInfoTab callbacks={wdState} />;
            case "Waste Info":
              return <WasteDisposalWasteInfoTab callbacks={wdState} />;
            case "Paper Work":
              return <WasteDisposalPaperWorkTab callbacks={wdState} />;
            default:
              return null;
          }
        })()}
      </div>
    </div>
  );
};
